import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import CheckmarkFilled32 from '@carbon/icons-react/es/checkmark--filled/32';
import { keyBy } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FormInstanceApi, FormResponseType, FormTypes } from 'api/form';
import { Button } from 'components/atoms/button';
import { Loading, PageLoading } from 'components/atoms/loading';
import { Page } from 'components/molecules/page/page';
import { toastService } from 'components/molecules/toast';
import { WebFormRenderer } from 'components/organisms/forms/web-form-renderer';
import { flattenQuestions } from 'components/organisms/templates/forms/utils';
import { ProductiveHeading03, ProductiveHeading05 } from 'components/styles';
import { useTranslate } from 'hooks/use-translation';

const SubmitWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex-direction: column;
`;
const FormSubmittedIcon = styled.div`
  svg {
    width: 128px;
    height: 128px;
    fill: var(--green-50);
  }
`;

const SubmitMessage = styled.div`
  ${ProductiveHeading03};
`;
const SubmitMessageDesc = styled.div`
  ${ProductiveHeading05};
`;
const ProjectName = styled.div`
  ${ProductiveHeading05};
  font-weight: bolder;
`;
const SubmitSuccessMessageWrapper = styled.div`
  margin: var(--spacing-08) 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  align-items: center;
`;

export const WebForm: FC = () => {
  const params = useParams<{ instanceHash: string }>();
  const translate = useTranslate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);

  const onAnswerSubmitSuccess = () => {
    setIsSubmitting(false);
    setIsSubmitSuccess(true);
  };

  const onAnswerSubmitFailure = () => {
    setIsSubmitting(false);
    toastService({
      title: translate('forms.response_submitted_failure'),
      message: '',
    }).error();
  };
  const { data: webFormInstance, isLoading: isWebFormLoading } = useQuery(
    ['web-form', params.instanceHash],
    () => {
      return FormInstanceApi.getWebFormData(params.instanceHash);
    },
    {
      onSuccess: (response) => {
        if (
          response.formType === FormTypes.FormPane ||
          response.formType === FormTypes.Invalid
        ) {
          const { taskDetails } = response;

          window.open(
            `/#task_drawer=${taskDetails.projectId}.${taskDetails.taskId}`,
            '_self',
          );
        }
      },
    },
  );

  const isMultiResponseModeForm =
    webFormInstance?.formInstance?.formTemplate.formSettings
      .formResponseType === FormResponseType.MultipleResponse;

  const flattenedQuestionsMap = useMemo(() => {
    const questions =
      webFormInstance?.formInstance?.formTemplate.formQuestions ?? [];

    if (questions) {
      const flattenedQuestions = flattenQuestions(questions);

      return keyBy(flattenedQuestions, 'questionId');
    }

    return {};
  }, [webFormInstance?.formInstance?.formTemplate.formQuestions]);

  return (
    <Page>
      {isWebFormLoading || !webFormInstance ? (
        <PageLoading />
      ) : (
        <Page.Content>
          {isSubmitting ? (
            <SubmitWrapper>
              <Loading size="large" />
              <SubmitMessage>
                <FormattedMessage id="forms.response.submitting_response" />
              </SubmitMessage>
            </SubmitWrapper>
          ) : isSubmitSuccess ? (
            <SubmitWrapper>
              <FormSubmittedIcon>
                <CheckmarkFilled32 />
              </FormSubmittedIcon>
              <SubmitSuccessMessageWrapper>
                <SubmitMessage>
                  <FormattedMessage id="thanks" />!
                </SubmitMessage>
                <SubmitMessageDesc>
                  <FormattedMessage id="forms.response.submitted_success" />
                </SubmitMessageDesc>
                <ProjectName>
                  {webFormInstance.taskDetails.projectName}
                </ProjectName>
              </SubmitSuccessMessageWrapper>
              {isMultiResponseModeForm && (
                <Button
                  type="primary"
                  size="large"
                  icon={<ArrowRight16 />}
                  iconPlacement="right"
                  onClick={() => {
                    setIsSubmitSuccess(false);
                  }}
                >
                  <FormattedMessage id="forms.response.submit_new_response" />
                </Button>
              )}
            </SubmitWrapper>
          ) : webFormInstance.formInstance ? (
            <WebFormRenderer
              flattenedQuestionsMap={flattenedQuestionsMap}
              formTemplate={webFormInstance.formInstance.formTemplate}
              formAnswerData={webFormInstance.formAnswer}
              formInstance={webFormInstance.formInstance}
              taskDetails={webFormInstance.taskDetails}
              isPreviewMode={false}
              companyName={webFormInstance.taskDetails.companyName}
              companyLogoUrl={webFormInstance.taskDetails.companyLogoUrl}
              setIsSubmitting={setIsSubmitting}
              onAnswerSubmitSuccess={onAnswerSubmitSuccess}
              onAnswerSubmitFailure={onAnswerSubmitFailure}
            />
          ) : null}
        </Page.Content>
      )}
    </Page>
  );
};
